// Client-only helper: relies on the browser sessionStorage API, so it must be
// called from the browser (e.g. inside a useEffect of a client component).
import getIpFromCloudflare, { type CloudflareTraceResult } from './getIpFromCloudflare';

const CACHE_KEY = 'cf-trace';

// Read a previously resolved trace from sessionStorage. We only trust the
// cached value when it carries a country code (e.g. loc=US); a missing loc
// means the lookup never fully succeeded, so we should query again.
function readCache(): CloudflareTraceResult | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CloudflareTraceResult;
    if (parsed?.loc) return parsed;
  } catch {
    // Ignore malformed cache / disabled storage (or SSR) and fall back to a request.
  }

  return null;
}

function writeCache(result: CloudflareTraceResult | null): void {
  if (!result?.loc) return;

  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
  } catch {
    // Ignore quota errors / disabled storage.
  }
}

// Dedupe concurrent calls so that multiple components mounting at the same time
// only trigger a single network request.
let inFlight: Promise<CloudflareTraceResult | null> | null = null;

/**
 * Client-side wrapper around {@link getIpFromCloudflare} that caches the result
 * in sessionStorage. If a cached trace with a country code already exists it is
 * returned without issuing a second request; otherwise the original lookup runs
 * and a successful result is cached for the rest of the session.
 */
export default async function getIpFromCloudflareClient(): Promise<CloudflareTraceResult | null> {
  const cached = readCache();
  if (cached) return cached;

  if (inFlight) return inFlight;

  inFlight = getIpFromCloudflare()
    .then((result) => {
      writeCache(result);
      return result;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}
